import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { AlertCircle } from 'lucide-react';
import { loginRequest } from '../../config/authConfig';
import { appConfig } from '../../config/appConfig';
import BlueMountainLogo from '../../assets/logo_clean.png';

interface LoginScreenProps {
  onLoginSuccess?: () => void;
}

/**
 * LoginScreen Component
 * 
 * Full-screen branded login screen for BlueMountain.
 * Displays when user is not authenticated and authentication is enabled.
 * 
 * Features:
 * - BlueMountain corporate branding
 * - Environment badge (Development/Production)
 * - Single sign-on via MSAL
 * - Error handling for login failures
 */
export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const { instance } = useMsal();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    setError(null);

    try {
      // Use redirect flow to avoid COOP/popup communication issues with
      // login.microsoftonline.com. The page will navigate away; MSAL's
      // initialize() processes the response when the user returns.
      await instance.loginRedirect(loginRequest);
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Handle specific error cases
      if (err.errorCode === 'user_cancelled') {
        setError('Login was cancelled. Please try again.');
      } else {
        setError('Unable to sign in. Please try again or contact support.');
      }
      setIsLoggingIn(false);
    }
  };

  // Determine environment label
  const environmentLabel = appConfig.environment === 'production' 
    ? 'PRODUCTION' 
    : 'DEVELOPMENT';
  
  const environmentColor = appConfig.environment === 'production'
    ? 'bg-red-600'
    : 'bg-gray-600';

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-[#0088CC] to-[#0066AA] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-white/5 rounded-full -top-48 -left-48 blur-3xl" />
        <div className="absolute w-96 h-96 bg-white/5 rounded-full -bottom-48 -right-48 blur-3xl" />
      </div>

      {/* Main content container */}
      <div className="relative z-10 flex flex-col items-center gap-8 px-4">
        {/* Environment Badge */}
        <div className={`${environmentColor} text-white px-4 py-2 rounded-lg text-sm font-semibold tracking-wide shadow-lg`}>
          {environmentLabel}
        </div>

        {/* Logo */}
        <div className="flex items-center justify-center mb-4">
          <img 
            src={BlueMountainLogo} 
            alt="BlueMountain Quality Resources" 
            className="h-24 w-auto drop-shadow-2xl"
          />
        </div>

        {/* Welcome tagline */}
        <div className="text-center mb-2">
          <h1 className="text-white text-2xl font-semibold mb-1">AI Prompt Library</h1>
          <p className="text-white/80 text-sm">BlueMountain Quality Resources</p>
        </div>

        {/* Sign in with Microsoft Button */}
        <button
          onClick={handleLogin}
          disabled={isLoggingIn}
          className="bg-white hover:bg-gray-50 text-gray-800 font-semibold text-base px-8 py-3 rounded-lg shadow-2xl transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-3 min-w-[240px] justify-center border border-gray-200"
          aria-label="Sign in with Microsoft account"
        >
          {isLoggingIn ? (
            <>
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Signing In...</span>
            </>
          ) : (
            <>
              {/* Microsoft logo squares */}
              <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#00a4ef"/>
                <rect x="1" y="11" width="9" height="9" fill="#7fba00"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              <span>Sign in with Microsoft</span>
            </>
          )}
        </button>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/90 text-white px-6 py-3 rounded-lg shadow-lg flex items-start gap-3 max-w-md animate-in fade-in slide-in-from-top-2 duration-300">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-semibold mb-1">Sign In Error</p>
              <p className="text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Dismiss error"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Help Text */}
        <p className="text-white/60 text-sm text-center max-w-md mt-2">
          Use your organizational Microsoft account to sign in
        </p>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 text-white/60 text-xs">
        © {new Date().getFullYear()} BlueMountain Quality Resources
      </div>
    </div>
  );
};
